import { useEffect, useState } from 'react';

import type { SavedListOutreachMessageDTO } from '@/entities/saved-creators-list';
import { Button } from '@/shared/components/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/shadcn/dialog';
import { Input } from '@/shared/components/shadcn/input';
import { Textarea } from '@/shared/components/shadcn/textarea';
import { Sparkles } from 'lucide-react';

type Props = {
  open: boolean;
  title: string;
  message: SavedListOutreachMessageDTO;
  includeSubject: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (message: SavedListOutreachMessageDTO) => void;
};

export function OutreachMessageDialog({
  open,
  title,
  message,
  includeSubject,
  onOpenChange,
  onSave,
}: Props) {
  const [subject, setSubject] = useState(message.subject ?? '');
  const [body, setBody] = useState(message.body);

  useEffect(() => {
    if (!open) return;
    setSubject(message.subject ?? '');
    setBody(message.body);
  }, [message.body, message.subject, open]);

  const handleSave = () => {
    onSave({
      subject: includeSubject ? subject : undefined,
      body,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[680px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="sr-only">
            Edit the outreach message template before starting outreach.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-5">
          {includeSubject ? (
            <label className="grid gap-2 text-sm font-medium">
              Subject
              <Input value={subject} onChange={(event) => setSubject(event.target.value)} />
            </label>
          ) : null}

          <label className="grid gap-2 text-sm font-medium">
            {includeSubject ? 'Body' : 'Message'}
            <div className="overflow-hidden rounded-md border">
              <Textarea
                aria-label={includeSubject ? 'Body' : 'Message'}
                className="min-h-44 rounded-none border-0 focus-visible:ring-0"
                value={body}
                onChange={(event) => setBody(event.target.value)}
              />
              <div className="border-border flex items-center justify-between border-t px-3 py-2">
                <span className="text-muted-foreground text-xs">
                  Add prompt for AI-generated content
                </span>
                <Button type="button" variant="ghost" size="sm" disabled>
                  <Sparkles className="size-4" />
                  Add AI Content
                </Button>
              </div>
            </div>
          </label>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} disabled={body.trim().length === 0}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
